import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import React from 'react';
import tw from 'tailwind.macro';
import Button from '../components/button';
import Layout from '../components/layout';
import LeadText from '../components/leadText';
import Section from '../components/section';
import SEO from '../components/seo';
import PageNavigator from '../components/pageNavigator';
import SafeLink from '../components/safeLink';
import Grid from '../components/grid';

const SocialJusticeChampion = ({ data, location }) => {
  const parentTitle = 'The Centre for Social Justice presents:';
  const pageTitle = 'Social Justice Champion Awards 2026';

  return (
    <Layout location={location} parentTitle={parentTitle} pageTitle={pageTitle}>
      <SEO title={pageTitle} image={data.hero.childImageSharp.fluid.src} />
      <div
        css={[
          tw`relative flex items-center py-10 bg-maroon`,
          `min-height:30vh;`,
        ]}
      >
        <div css={tw`absolute inset-0 z-0`}>
          <video
            autoPlay
            muted
            loop
            playsInline
            css={tw`w-full h-full object-cover`}
          >
            <source src="/adobestock_365452886.webm" type="video/webm" />
            <source src="/adobestock_365452886.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="relative container">
          <div>
            <LeadText
              css={[
                tw`my-0 uppercase tracking-wide text-white`,
                `text-shadow:0 0 10px rgba(0,0,0,0.5)`,
              ]}
              className="text-shadow"
            >
              {parentTitle}
            </LeadText>
            <h1
              className="text-shadow"
              css={[
                tw`my-0 text-white tracking-wider`,
                `text-shadow:0 0 10px rgba(0,0,0,0.5)`,
              ]}
            >
              {pageTitle}
            </h1>
          </div>
        </div>
      </div>
      <PageNavigator
        items={{
          welcome: 'Welcome',
          categories: 'Award categories',
          judges: 'Judges',
          contact: 'Contact',
        }}
        buttonText="Nominate Now"
        buttonLink="#categories"
      />
      <Section id="welcome">
        <Grid lgGridCols={2} lgGap={5}>
          <div css={tw`self-center`}>
            <LeadText>
              The Social Justice Champion awards, in collaboration with Champion
              South Africa, were created to honour individuals and organisations
              who are making outstanding contributions toward advancing social
              justice in South Africa—many of them unsung heroes working
              tirelessly to improve the lives of others. At the CSJ, we define
              social justice—drawing from the work of Professor Thuli Madonsela—
              as: “Embracing every person’s humanity through equal enjoyment of
              all rights and freedoms reflected in the just, equitable and fair
              distribution of all opportunities, resources, privileges,
              benefits, and burdens within and between societies.”
            </LeadText>
            <ul>
              <li>
                <strong>Awards ceremony:</strong> 12 October 2026
              </li>
              <li>
                <strong>Venue:</strong> Stellenbosch, South Africa
              </li>
              <li>
                <strong>Nominations close:</strong> 12 September 2026
              </li>
            </ul>
          </div>
          <Img
            fluid={data.hero.childImageSharp.fluid}
            alt="Social Justice Champion Awards 2026, 12 October 2026, Stellenbosch"
            css={tw`rounded-lg shadow-lg`}
          />
        </Grid>
      </Section>
      <Section id="categories" isShaded>
        <h2>Award categories</h2>
        <LeadText>
          We invite nominations for outstanding individuals and organizations
          who are making significant contributions to social justice in South
          Africa. Choose the appropriate category below to submit your
          nomination. Nominations are open until 12 September 2026.
        </LeadText>
        <Grid css={tw`items-start`}>
          <div css={tw`mb-auto p-5 bg-white rounded-lg shadow-lg`}>
            <h3>Social Justice Champion of the Year 2026</h3>
            <Button to="https://forms.gle/qGkLRXh7AzeTRxDV9" size="md">
              Nominate now
            </Button>
            <p>
              A Social Justice Champion is someone who has moved the needle
              consistently in 2026 to advance equality and reduce poverty, a
              person who has shown a commitment to justice as justice for all,
              while galvanising others to pull together in a socially cohesive
              manner. Past recipients include:
            </p>
            <ul>
              <li>
                <strong>Dr Sheena Swemmer,</strong> 2025 Social Justice Champion
                of the Year
              </li>
              <li>
                <strong>Eon Hendrikse,</strong> social justice activist and
                Director of Idas Valley Community Trails
              </li>
              <li>
                <strong>Kabelo Mahlobogwane,</strong> Founder of the Marking App
              </li>
              <li>
                <strong>Dr Imtiaz Sooliman,</strong> Founder of the Gift of the
                Givers
              </li>
              <li>
                <strong>Lorenzo Davids,</strong> former Community Chest CEO
              </li>
              <li>
                <strong>Lorraine Khoza,</strong> founder of a non-profit
                tackling gender-based violence
              </li>
              <li>
                <strong>Dr Athol Williams,</strong> Read to Rise co-founder and
                whistleblower against state capture
              </li>
            </ul>
          </div>
          <div css={tw`p-5 bg-white rounded-lg shadow-lg`}>
            <h3>Bridge Builder of the Year 2026</h3>
            <Button to="https://forms.gle/ruVRuYatpGSFJBW27" size="md">
              Nominate now
            </Button>
            <p>
              Established in memory of Dr Beatrice Wiid, a member of COSOC, who
              excelled in bridging divides between communities separated by
              economic and historical racial disparities, this award recognises
              a person who has achieved extraordinary heights in healing the
              divisions of the past by building social bridges across
              historically divided groups in society through using their social
              capital and related power to heal social divisions and break walls
              between groups through facilitating interface between groups and
              advancing social justice. Past recipients include:
            </p>
            <ul>
              <li>
                <strong>Sonja Cilliers,</strong> 2025 Bridge Builder Award
                recipient
              </li>
              <li>
                <strong>Dr Louise van Rhyn,</strong> whose Partners for
                Possibility programme has connected school principals and
                business leaders to improve education in historically
                disadvantaged areas.
              </li>
              <li>
                <strong>The YearBeyond initiative,</strong> a partnership
                between the Western Cape Government, Community Chest, Michael &
                Susan Dell Foundation and NGOs, creating work and education
                pathways for unemployed youth while fostering a culture of
                community service.
              </li>
              <li>
                <strong>Eugene Opperman,</strong> for bridging the knowledge gap
                in maintenance law through the innovative Lunch and Learn
                platform.
              </li>
            </ul>
          </div>
          <div css={tw`mb-auto p-5 bg-white rounded-lg shadow-lg`}>
            <h3>Social Justice Rising Star Award 2026</h3>
            <Button to="https://forms.gle/d2L9B3DSGTi6fccU8" size="md">
              Nominate now
            </Button>
            <p>
              This award seeks to recognise a youthful individual, as defined by
              South African standards, committed to fostering a just and
              equitable environment. Age is not a barrier for this individual,
              who effectively utilises available platforms and resources to
              address inequality within their communities. This award aims to
              acknowledge and celebrate the efforts of young leaders striving to
              create greater equity in their communities and beyond. This
              includes leveraging social media, engaging in public speaking,
              organising initiatives and collaborating with local organisations
              to drive meaningful change.
            </p>
            <p>
              The inaugural 2024 award was presented to{' '}
              <strong>Karabo Mokgonyana</strong>, a dynamic leader who has
              worked across human rights, climate justice, social and economic
              rights, gender equality and governance. In 2025, the Rising Star
              award was presented to <strong>Chevano Frans</strong> and{' '}
              <strong>Lesego Louw</strong>.
            </p>
          </div>
        </Grid>
      </Section>
      <Section id="judges">
        <h2>The judges</h2>
        <Grid
          mdGridCols={1}
          lgGridCols={1}
          css={tw`items-start`}
          gap={5}
          mdGap={10}
          lgGap={16}
        >
          <div>
            <Img
              fixed={data.thuliMadonsela.childImageSharp.fixed}
              css={tw`mb-3 rounded-full`}
            />
            <h3>Prof Thuli Madonsela</h3>
            <p>
              Professor Thuli (Thulisile) Nomkhosi Madonsela is the director of
              the Centre for Social Justice (CSJ) in the Faculty of Law at
              Stellenbosch University (CSU) where she occupies is the Law Trust
              Chair Professor of Law in Social Justice Studies. She currently
              teaches on Constitutional Law, Social Justice, Administrative Law,
              Constitutional Governance and Ethical Leadership. She holds eight
              honorary law doctorates in addition to two law degrees, a Harvard
              Advanced Leadership Certificate and other post graduate
              qualifications. She is also an advocate of the High Court of South
              Africa.
            </p>
            <p>
              The former Public Protector of South Africa and full-time
              Commissioner in the South African Law Reform Commission, Prof
              Madonsela is a member of the African Academy of Sciences and the
              Scottish WS Society. She is the Chairperson of Cities Alliance and
              a member of the UN Scientific Advisory Board, International
              Anticorruption Conference Coordinating and the International
              Council for Arbitration in Sport (ICAS).
            </p>
            <p>
              She was one the 11 technical experts who helped draft the South
              African Constitution and is a co-architect of several
              constitutionally mandated laws, including the Equality Act,
              Employment Equity Act and Promotion of Administrative Justice Act.
              A regularly sought keynote speaker, Prof Madonsela has written,
              taught, presented papers and published for decades on these
              matters.
            </p>
            <p>
              Prof Madonsela helped draft several international human rights
              instruments at UN, AU and SADC level and country reports on such
              matters. She is an author of two books on Gender Mainstreaming
              (2002) and Gender Management (2002) and a core author and
              architect of the inaugural Benchwork for Equality Courts and
              Resource Book for Equality Court Clerks.
            </p>
            <p>
              Named one of Time 100 most influence persons in the world in 2014
              and Forbes Africa Person of the year in 2017, Prof Madonsela's
              global recognition includes over 70 awards, that include Rotary's
              Paul Harris Award, Commonwealth Lawyers Truth and Justice Award,
              and the global ACFE Cressy Award. Prof Madonsela is a mother of
              two, a philanthropist and avid mountaineer who has summited Mt
              Kilimanjaro twice under #Trek4Mandela-#Caring4Girls and
              #Action4Inclusion. She was a monthly columnist for the Financial
              Mail, remains one for the City Press and occasionally writes for
              other newspapers. This lecture was made possible by social justice
              research funding provided to the Chair and CSJ by the
              Vice-Chancellor's Strategic Fund at SU.
            </p>
          </div>
          <div>
            <Img
              fixed={data.ashrafGarda.childImageSharp.fixed}
              css={tw`mb-3 rounded-full`}
            />
            <h3>Ashraf Garda</h3>
            <p>
              Ashraf, founder of Champion South Africa, is a household name
              known for using storytelling and incisive questioning to create
              impactful conversation. As the host of the National Pulse on SAfm
              Radio, he engages the nation daily, breaking down complex issues
              into relatable narratives. His mission is to inspire "champion
              people" to build a champion South Africa, driving a movement for
              national purpose and unity.
            </p>
            <p>
              As a highly sought-after speaker and moderator, Ashraf has taken
              centre stage at prestigious global events, including serving as
              Programme Director for the BRICS Summits in South Africa (2018 and
              2023) and moderator at the 2023 One Young World Summit in Montreal
              Canada. His ability to facilitate meaningful dialogue has made him
              a trusted voice at high-profile gathering like the Intra-Trade
              Fair (IATF) 2023 IN Cairo and the 2024 Annual Social Justice
              Lecture delivered by Public Protector Adv Kholeka Gcaleka.
            </p>
            <p>
              Through Champion South Africa, Ashraf launched the
              #WeChampionsSouthAfrica campaign in April 2025, rallying 100
              influential South Africans to share how they have contributed to
              nation building. As member of Counsel of Champions at the Centre
              for Social Justice chaired by Prof Thuli Madonsela and a judge for
              the Social Justice Champions Awards, he continues to drive change.
              Ashraf was also recognised as 2023 Treeshake change maker for his
              work that continues to inspire united, purpose driven change.
            </p>
          </div>
          <div>
            <Img
              fixed={data.johnKani.childImageSharp.fixed}
              css={tw`mb-3 rounded-full`}
            />
            <h3>John Kani</h3>
            <p>
              John Kani is an actor, a director and a playwright. He was born in
              New Brighton Port Elizabeth, in 1943, where he started his career
              with the Serpent Players Drama Group. He joined the Market Theatre
              in Johannesburg in 1986. As well as acting in SIZWE BANZI IS DEAD
              and THE ISLAND, John also co-wrote the plays with Athol Fugard and
              Winston Ntshona. John won the 1974/75 TONY AWARD On Broadway for
              Best Actor for his performance in the productions he also received
              an Obie Award in New York for the Island. Returning to South
              Africa he was detained and kept in solitary confinement by the
              South African Security Police and was only released because
              massive demonstrations around the world demanding his release. In
              1981 the survive an assignation attempt by the Secrete Police
              which left him eleven stab wounds. In 1985 he was harassed by the
              secrete police for kissing a white actress on stage while
              performing Miss Julie by Johan Strindberg. The same-thing happened
              in 1987 when he played Othello by William Shakespeare for kissing
              Desdemona (white actress).
            </p>
            <p>
              In 1989 together with the late Barney Simon, co- founder of the
              Market Theatre, he founded the Market Theatre Laboratory, a drama
              school for young black people who would not be able to study at
              universities through lack high school education and financial
              resources. The school has now become the crucible of new talents
              and new works with an extensive outreach program. The school
              celebrated 30 years in 2019 and has produced more than 300 actors
              working in the Theatre, film and Television Industry in South
              Africa.
            </p>
            <p>
              In 1995 John was awarded an Honorary Doctorate of Philosophy from
              the University of Durban Westville and in April, 1998 was awarded
              an Honorary Doctorate of Literature from Rhodes Rhodes University,
              Grahamstown.
            </p>
            <p>
              In June 2006 John received an Honorary Doctorate of Literature
              from the University of Cape Town. John received the Olive Shriner
              Prize Award for writing Nothing But The Truth from the English
              Academy of Southern Africa. Just recently the play was made into a
              film, directed by John Kani and has a number of awards including
              the SILVER STALLION at FESPACO, Burkina Faso March 2009, Best Film
              in the Milan International Film Festival and also Best film at the
              Cameroon Film Festival 2009. John was awarded the inaugural
              Ousmane Sembene Award at FESPACO 2009 for his contribution to the
              African film industry.
            </p>
            <p>
              On the 27th September2005 John received the Order Of IKamanga
              Silver from the President of the Republic of South Africa Mr.
              Thabo Mbeki recognising his contribution in the struggle for the
              liberation of his country through his work in the arts especially
              his contribution to a free, non racial non sexist and democratic
              South Africa. Subsequently John was appointed by the President to
              serve on the Presidential Advisory Council of National Orders and
              Awards.
            </p>
            <p>
              The Nelson Mandela University awarded him a PhD honorary for his
              contribution to the arts and to the struggle for the liberation of
              his country. In June 2015 the Market Theatre Foundation honoured
              him by naming the Main Theatre- The John Kani Theatre. In 2017 the
              Nelson Mandela Metropolitan Council of Port Elizabeth renamed
              Whites Road in the city centre to the John Kani Road in honour of
              his contribution to the liberation of his country.
            </p>
            <p>
              On the 29th March 2018 John Kani was awarded the JOHN F KENNEDY
              GOLD MEDAL AWARD for his contribution to Humanity and Excellence
              in the Arts. On the 3rd December 2018 he received the SOTIGUI
              HONOR CINEMA AFRIQUE AWARD in Ouagadougou Burkina Faso for his
              contribution to the advancement of the African Film Industry.
            </p>
            <p>
              In 2019 he received the VOICES OF FREEDOM in New York for his
              contribution to peace through dialogue, previous recipients
              include Nelson Mandela, Bishop Tutu and Harry Belafonte Prof Thuli
              Mandonsela.
            </p>
            <p>
              His latest play, KUNENE AND THE KING ,which he wrote and performed
              in it with Sir Antony Sher opened at the Royal Shakespeare Company
              in Stratford and the West End to the Ambassador Theatre in London
              in January 2020. Kunene opened at the Joburg Theatre and toured
              theatres around the country. Wits University has awarded him with
              Doctorate in Literature honoris causa for his contribution to the
              Arts 2021 He was awarded the Pragnell Shakespeare Birthday Award
              for making Shakespeare accessible to broader Communities.
              Audiences. His latest play Kunene and the King was included in the
              ten highlights of the 60th Anniversary of the Royal. Shakespeare
              Company.
            </p>
            <p>
              2024 he was warded the Ubuntu Cultural Award- Legend as a Cultural
              Ambassador by the Department of International Relations and
              Cooperations. 2023 King Charlse the Third bestowed upon him the
              The Officer of the Most Excellent Order of the British Empire-
              OBE- for his contribution to the Arts. In 2024 at the Silicon
              Valley African Film Festival in San Jose together with Souleymane
              Cisse from Mali he was awarded the Cultural Icon Award for their
              contribution to the African Film Industry.
            </p>
          </div>
          <div>
            <Img
              fixed={data.louiseVanRhyn.childImageSharp.fixed}
              css={tw`mb-3 rounded-full`}
            />
            <h3>Dr Louise Van Rhyn</h3>
            <p>
              Louise is a visionary leadership and original development
              consultant, renowned for her commitment to fostering impactful
              sustainable change in South Africa and beyond. She is the founder
              of Symphonia and the designer of the globally recognised Partners
              for Possibility (PfP) program, a pioneering initiative that brings
              together business leaders and school principals to transform under
              resourced schools. Under her leadership, PfP has been acknowledged
              as one of the world’s best social-change initiatives, earning
              numerous accolades, including recognition by WISE (World
              Innovation Summit for Education). With a doctorate in complex
              systems from the University of Hertfordshire under Professor Ralph
              Stacey. Louise has deep expertise in understanding organisations
              as a dynamic responsive process of relating. She has applied her
              expertise to lead large-scale, systemic change, blending academic
              rigour with hands-on experience. Driven with her belief that South
              Africa is "Alive with Possibility”. Louise is passionate about
              leadership as a force for societal transformation. She integrates
              neuroscience, psychology and human-centred approaches to create
              environments where people can flourish and contribute their best.
              An inspiring speaker, mentor and community builder. Louise
              continues to work tirelessly to advance her vision of a more
              equitable and thriving South Africa rooted in purpose, partnership
              and possibility.
            </p>
          </div>
          <div>
            <Img
              fixed={data.basetsanaKumalo.childImageSharp.fixed}
              css={tw`mb-3 rounded-full`}
            />
            <h3>Basetsana Kumalo</h3>
            <p>
              Businesswoman, Author, and Philanthropist. The Founder and CEO of
              Basetsana Women Investment Holdings (BWIH) Pty Ltd, a private
              equity firm with a diverse portfolio in fintech, media, mining,
              travel and tourism. Over three decades, the serial entrepreneur
              has created thousands of jobs through her various companies. In
              every industry she has entered, each platform she has made an
              indelible impact on the lives of women in South Africa.
            </p>
            <p>
              She is the Chairperson of the Business Women's Association of
              South Africa (BWA), the largest business women's network in the
              country, which was established 40 years ago. This organization has
              made a tangible impact in the advancement and empowerment of women
              so that women become part of the mainstream of the economy. Across
              diverse industries such as media, real estate, technology, and
              mining a career spanning 30 years has seen Basetsana lead,
              collaborate, and fight tenaciously to build a legacy that has been
              recognized by UNICEF, the World Economic Forum, Stellenbosch
              University and the Nelson Mandela Institute for Rural Education
              and Development.
            </p>
            <p>
              Bassie's memoir, My Journey of Hope, received rave reviews
              countrywide and made it one of the best sellers. Bassie chronicles
              the story of a young girl thrust onto the world stage when she
              became Miss South Africa in 1994, 1st runner up in Miss World
              1994. That opportunity led to a television career whose highs
              include interviewing music legends Michael Jackson and Luther
              Vandross, talk show queen Oprah Winfrey, including all the South
              African Presidents including Nelson Mandela.
            </p>
            <p>
              Passionate about entrepreneurship, mentorship, and women's
              development and building the next generation of leaders, she has
              directly mentored and educated many young people, the bulk of them
              women, who have gone on to excel in their respective fields.
            </p>
            <p>
              She is the Patron of IamtheCode, the first African-led global
              movement to mobilize governments, the private sector,
              philanthropic foundations, investors, and civil society to advance
              STEAMD (Science, Technology, Engineering, Arts, Mathematics, and
              Design) education. Its goal is to mobilize these groups to invest
              in future technologies that can drive sustainable development for
              women and girls in marginalized communities. The wife and mother
              of three sits on various boards among them the Nelson Mandela
              Institute for Education and Rural Development, which fights for
              the education of children from some of South Africa's poorest
              communities.
            </p>
            <p>
              She serves as Co-Chair of the Council of Social Justice Champions,
              Stellenbosch University. She holds a Bachelor of Arts Degree
              (Politics and Communications) from the University of South Africa,
              a Certificate in Executive Education on Global Leadership from
              Harvard University's Kennedy School of Government, and a
              Certificate in Leadership and Decision Making from Yale
              University. She is currently doing her Doctorate with Middlesex
              University in London.
            </p>
          </div>
          <div>
            <Img
              fixed={data.zinziMgolodela.childImageSharp.fixed}
              css={tw`mb-3 rounded-full`}
            />
            <h3>Zinzi Mgolodela</h3>
            <p>
              A Champion of Corporate Social Justice. Director of Corporate
              Social Justice at Woolworths Holdings Limited – a JSE listed
              Company with operations in Africa and Australasia. Zinzi
              Mgolodela's work focuses on systemic inequality. She leads the
              Inclusive Justice Initiative (IJI) to foster social justice for
              inclusive workplaces, value chains, customer experience and
              impactful community development.
            </p>
            <p>
              Previously, as Director of Corporate Affairs, she built value
              creating partnerships with stakeholders across Africa. A member of
              the Council for Social Justice Champions. Appointed in 2020 to the
              Commission for Employment Equity. Serves as Chairperson of the
              Restitution Foundation. A mother of three, she champions social
              justice both professionally and personally.
            </p>
          </div>
          <div>
            <Img
              fixed={data.thembalethiSeyisi.childImageSharp.fixed}
              css={tw`mb-3 rounded-full`}
            />
            <h3>Thembalethu Seyisi</h3>
            <p>
              Thembalethu Seyisi is a multifaceted individual who has dedicated
              his life to making a positive impact in society. As an Admitted
              Attorney of the High Court of South Africa and Head: Law Reform,
              Advocacy and Institutional Partnerships at the Centre for Social
              Justice, Stellenbosch University, he is
              at the forefront of driving social justice reforms in the academic
              field and beyond. Thembalethu's leadership qualities were evident
              from an early age when he was selected as the Head Prefect at
              Oaklands High School, where he excelled academically.
            </p>
            <p>
              From 2018 to July 2021, he served as a Board Member at Salesian
              Life Choices, a non-profit organisation focused on disrupting
              inequality through youth empowerment. In this role, he ensured
              good governance and accountability. In 2020, Thembalethu
              co-founded the Black Lawyers Association Stellenbosch University
              Student Chapter (BLASC), aimed at transforming legal education,
              enhancing the student experience and advancing human rights. He is
              also a Director of Academic Inclusion Drive (AID), a non-profit
              organisation in support of the #Action4Inclusion campaign, a
              Stellenbosch University crowdfunding initiative dedicated to
              alleviating student debt for the 'missing middle' students.
            </p>
            <p>
              As a Values20 (V20) Solidarity Stream Lead for Advocacy,
              Thembalethu continues to make contributions to the advancement of
              equality, solidarity and sustainable development on a global
              stage. His advocacy is evident in parliamentary submissions and
              opinion articles for prominent news platforms, such as Mail &
              Guardian, News24, Network24 and Daily Maverick.
            </p>
            <p>
              At the young age of 21, Thembalethu became a self-published author
              of a book titled 21 Life Lessons (@ 21, in which he asked
              successful people he admires to share insights they wished they
              had known at 21. The book features contributions from prominent
              figures including Justice Edwin Cameron, Professor Madonsela,
              Zelda la Grange, Sandra Prinsloo, and former UCT Vice-Chancellor
              Professor Mamokgethi Phakeng.
            </p>
            <p>
              Thembalethu's dedication and achievements have been recognised
              through numerous accolades, including the Stellenbosch University
              Rector's award for excellence in social impact (2021), News24's 30
              Young Mandelas (2022) and being listed in the Mail and Guardian's
              Top 200 Young South Africans (2023). As a young leader,
              Thembalethu embodies the values of social justice, leadership and
              advocacy. As a Master's student investigating the role of the
              South African Human Rights Commission in championing social
              justice, his multifaceted contributions to society serve as an
              inspiration to many and pave the way for a more just South Africa.
            </p>
          </div>
        </Grid>
      </Section>
    </Layout>
  );
};

export default SocialJusticeChampion;

export const pageQuery = graphql`
  query {
    hero: file(
      relativePath: { eq: "2026-10-social-justice-champion-banner.jpg" }
    ) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    louiseVanRhyn: file(relativePath: { eq: "louise-van-rhyn.jpg" }) {
      childImageSharp {
        fixed(width: 150, height: 150) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    ashrafGarda: file(relativePath: { eq: "ashraf-garda.jpg" }) {
      childImageSharp {
        fixed(width: 150, height: 150) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    basetsanaKumalo: file(relativePath: { eq: "basetsana-kumalo.jpg" }) {
      childImageSharp {
        fixed(width: 150, height: 150) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    johnKani: file(relativePath: { eq: "john-kani.jpg" }) {
      childImageSharp {
        fixed(width: 150, height: 150) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    thuliMadonsela: file(relativePath: { eq: "thuli-madonsela.jpg" }) {
      childImageSharp {
        fixed(width: 150, height: 150) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    zinziMgolodela: file(relativePath: { eq: "zinzi-mgolodela.jpg" }) {
      childImageSharp {
        fixed(width: 150, height: 150) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    thembalethiSeyisi: file(relativePath: { eq: "thembalethi-seyisi.jpg" }) {
      childImageSharp {
        fixed(width: 150, height: 150) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    site {
      siteMetadata {
        title
      }
    }
  }
`;
